import type { CountryRivalry } from '@/types';

// ---------------------------------------------------------------------------
// Country Rivalry & Bilateral Tensions Database
// Sources: SIPRI, IISS, Council on Foreign Relations, Crisis Group
// Updated: March 2026
// ---------------------------------------------------------------------------

export const COUNTRY_RIVALRIES: CountryRivalry[] = [

  // ── USA ────────────────────────────────────────────────────────────────────
  {
    id: 'usa-china',
    country1: 'US', country1Name: 'United States',
    country2: 'CN', country2Name: 'China',
    type: ['territorial', 'economic', 'ideological', 'cyber'],
    intensity: 'critical',
    description: 'Strategic competition across trade, technology, military, and Taiwan.',
    since: '2017',
    activeMilitary: false,
    keyIssues: ['Taiwan strait tensions', 'South China Sea FONOPs', 'Tech war (chips/AI)', 'Trade tariffs 2025', 'AUKUS vs PLAN'],
    latestIntel: 'US 7th Fleet patrols Taiwan Strait monthly. TSMC export controls tightened. Xi-Trump call stalled.',
    simulationEnabled: true,
  },
  {
    id: 'usa-russia',
    country1: 'US', country1Name: 'United States',
    country2: 'RU', country2Name: 'Russia',
    type: ['proxy-war', 'nuclear', 'sanctions', 'cyber'],
    intensity: 'critical',
    description: 'Cold War 2.0 — proxy war in Ukraine, NATO expansion, nuclear brinkmanship.',
    since: '2014',
    activeMilitary: true,
    keyIssues: ['Ukraine military aid', 'NATO Article 5', 'SWIFT sanctions', 'Nord Stream aftermath', 'Nuclear posture'],
    latestIntel: 'US provided 61B Ukraine package. Russia testing hypersonic Oreshnik vs NATO deterrence.',
    simulationEnabled: true,
  },
  {
    id: 'usa-iran',
    country1: 'US', country1Name: 'United States',
    country2: 'IR', country2Name: 'Iran',
    type: ['nuclear', 'sanctions', 'proxy-war'],
    intensity: 'high',
    description: 'Nuclear breakout prevention, maximum pressure sanctions, proxy conflicts via Hezbollah/Houthis.',
    since: '1979',
    activeMilitary: false,
    keyIssues: ['Nuclear JCPOA collapse', 'Uranium enrichment 90%', 'Proxy militia network', 'Strait of Hormuz'],
    latestIntel: 'Iran enriching at 60%+. IAEA monitoring breached. US 5th Fleet on high alert.',
    simulationEnabled: true,
  },
  {
    id: 'usa-nk',
    country1: 'US', country1Name: 'United States',
    country2: 'KP', country2Name: 'North Korea',
    type: ['nuclear', 'sanctions'],
    intensity: 'high',
    description: 'DPRK nuclear deterrence vs US extended deterrence for South Korea/Japan.',
    since: '1950',
    activeMilitary: false,
    keyIssues: ['ICBM tests', 'Nuclear warhead miniaturization', 'DPRK-Russia arms deal', 'Kim-Putin summit'],
    latestIntel: 'North Korea supplied 1M+ artillery shells to Russia. Kim received Russian satellite technology.',
    simulationEnabled: true,
  },

  // ── China ──────────────────────────────────────────────────────────────────
  {
    id: 'china-taiwan',
    country1: 'CN', country1Name: 'China',
    country2: 'TW', country2Name: 'Taiwan',
    type: ['territorial', 'war'],
    intensity: 'critical',
    description: 'PRC claims Taiwan as breakaway province. Taiwan maintains de facto independence.',
    since: '1949',
    activeMilitary: false,
    keyIssues: ['ADIZ violations (daily)', 'Amphibious invasion timeline 2027?', 'TSMC destruction scenario', 'US arms sales'],
    latestIntel: 'PLA exercises 120+ sorties/month near Taiwan. USS Roosevelt CSG in SCS. Taiwan Strait patrol normalized.',
    simulationEnabled: true,
  },
  {
    id: 'china-india',
    country1: 'CN', country1Name: 'China',
    country2: 'IN', country2Name: 'India',
    type: ['territorial', 'economic'],
    intensity: 'high',
    description: 'Himalayan border disputes — Aksai Chin, Arunachal Pradesh, LAC standoffs.',
    since: '1962',
    activeMilitary: false,
    keyIssues: ['Galwan Valley 2020 clash', 'LAC infrastructure buildup', 'Doklam plateau', 'QUAD vs SCO'],
    latestIntel: 'Disengagement at Depsang plains 2024. PLA building roads in disputed areas. QUAD naval exercises intensified.',
    simulationEnabled: true,
  },
  {
    id: 'china-japan',
    country1: 'CN', country1Name: 'China',
    country2: 'JP', country2Name: 'Japan',
    type: ['territorial', 'economic'],
    intensity: 'high',
    description: 'Senkaku/Diaoyu Islands dispute; PLAN gray zone operations in East China Sea.',
    since: '1895',
    activeMilitary: false,
    keyIssues: ['Senkaku/Diaoyu sovereignty', 'PLAN submarine incursions', 'Fukushima water discharge dispute', 'Japan rearmament'],
    latestIntel: 'Japan doubled defense budget to 2% GDP. JASDF intercepts PLAAF 700+/year. Coast Guard standoffs monthly.',
    simulationEnabled: true,
  },
  {
    id: 'china-philippines',
    country1: 'CN', country1Name: 'China',
    country2: 'PH', country2Name: 'Philippines',
    type: ['territorial'],
    intensity: 'high',
    description: 'South China Sea: China ignores 2016 UNCLOS ruling; water cannon attacks on Philippine resupply ships.',
    since: '2009',
    activeMilitary: false,
    keyIssues: ['Second Thomas Shoal', 'Water cannon incidents', 'Scarborough Shoal blockade', 'US-Philippine mutual defense'],
    latestIntel: 'China coast guard used water cannon 15x in 2025. US-PH Balikatan exercises expanded. Marcos pursuing dual-track.',
    simulationEnabled: true,
  },

  // ── Russia ─────────────────────────────────────────────────────────────────
  {
    id: 'russia-ukraine',
    country1: 'RU', country1Name: 'Russia',
    country2: 'UA', country2Name: 'Ukraine',
    type: ['war', 'territorial'],
    intensity: 'critical',
    description: 'Full-scale invasion since Feb 2022. Russia occupies ~18% of Ukrainian territory.',
    since: '2014',
    activeMilitary: true,
    keyIssues: ['Frontline Zaporizhzhia/Kherson', 'F-16s in combat', 'Kursk incursion', 'Ceasefire negotiations', 'NATO Article 5 threshold'],
    latestIntel: 'Russia advancing 300m/day in Donetsk. Ukraine Kursk bridgehead. Trump ceasefire pressure Jan 2025.',
    simulationEnabled: true,
  },
  {
    id: 'russia-nato',
    country1: 'RU', country1Name: 'Russia',
    country2: 'NATO', country2Name: 'NATO Alliance',
    type: ['ideological', 'nuclear', 'proxy-war'],
    intensity: 'critical',
    description: 'Russia vs NATO expansion — existential threat framing; nuclear deterrence at highest since Cold War.',
    since: '1991',
    activeMilitary: false,
    keyIssues: ['Article 5 credibility', 'Baltic states vulnerability', 'Kaliningrad corridor', 'Nuclear posture document Nov 2024'],
    latestIntel: 'Russia updated nuclear doctrine to allow first use vs conventional threat. Finland/Sweden in NATO. Baltic air policing tripled.',
  },

  // ── Middle East ────────────────────────────────────────────────────────────
  {
    id: 'israel-iran',
    country1: 'IL', country1Name: 'Israel',
    country2: 'IR', country2Name: 'Iran',
    type: ['proxy-war', 'nuclear', 'cyber'],
    intensity: 'critical',
    description: 'Shadow war: Israel strikes Iran nuclear sites; Iran proxies attack Israel via Hamas/Hezbollah/Houthis.',
    since: '1979',
    activeMilitary: true,
    keyIssues: ['Oct 7 aftermath', 'Iran nuclear breakout', 'Hezbollah rearmament', 'Direct missile exchanges Apr/Oct 2024'],
    latestIntel: 'Israel struck Iranian air defenses Oct 2024. Iran proxies degraded but rebuilding. Netanyahu nuclear option discussed.',
    simulationEnabled: true,
  },
  {
    id: 'israel-hamas',
    country1: 'IL', country1Name: 'Israel',
    country2: 'PS', country2Name: 'Hamas/Palestine',
    type: ['war', 'territorial'],
    intensity: 'critical',
    description: 'Gaza War after Oct 7 2023 attack. Phase 1 ceasefire Jan 2025. Humanitarian crisis ongoing.',
    since: '2007',
    activeMilitary: false,
    keyIssues: ['Ceasefire Phase 1', 'Hostage deal', 'Gaza reconstruction', 'Two-state solution dead?', 'ICC warrant'],
    latestIntel: 'Phase 1 ceasefire Jan 19 2025. 133 hostages; ~1100 returned. Netanyahu ICC warrant issued.',
  },
  {
    id: 'saudi-iran',
    country1: 'SA', country1Name: 'Saudi Arabia',
    country2: 'IR', country2Name: 'Iran',
    type: ['proxy-war', 'ideological', 'economic'],
    intensity: 'high',
    description: 'Sunni-Shia regional rivalry; proxy conflict in Yemen, Iraq. China-brokered rapprochement 2023.',
    since: '1979',
    activeMilitary: false,
    keyIssues: ['Yemen proxy war', 'Houthi missile threat', 'China brokered deal 2023', 'OPEC oil policy conflict'],
    latestIntel: 'Saudi-Iran embassies reopened. Yemen Houthis still attacking under Iranian guidance. Oil diplomacy tension.',
  },

  // ── Asia-Pacific ───────────────────────────────────────────────────────────
  {
    id: 'india-pakistan',
    country1: 'IN', country1Name: 'India',
    country2: 'PK', country2Name: 'Pakistan',
    type: ['territorial', 'nuclear', 'war'],
    intensity: 'high',
    description: 'Kashmir dispute; two nuclear-armed states with history of three wars. Cross-border terrorism ongoing.',
    since: '1947',
    activeMilitary: false,
    keyIssues: ['Kashmir LoC skirmishes', 'Pakistani-backed militant groups', 'Water treaty disputes', 'Nuclear first-use doctrine'],
    latestIntel: 'LOC ceasefire 2021 holding but tensions from Pulwama/Balakot precedent. Pakistan economic crisis limits capacity.',
    simulationEnabled: true,
  },
  {
    id: 'nk-sk',
    country1: 'KP', country1Name: 'North Korea',
    country2: 'KR', country2Name: 'South Korea',
    type: ['war', 'ideological'],
    intensity: 'high',
    description: 'Technically at war since 1950 armistice. DPRK balloon/trash provocations; ballistic missile tests.',
    since: '1950',
    activeMilitary: false,
    keyIssues: ['DMZ balloon provocations', 'DPRK ICBM tests', 'Korean unification dead?', 'DPRK-Russia arms deal effect on SK'],
    latestIntel: 'North Korea sent 2M+ artillery shells to Russia. Kim Jong-un declared South Korea "hostile state" Nov 2023.',
    simulationEnabled: true,
  },
  {
    id: 'nk-japan',
    country1: 'KP', country1Name: 'North Korea',
    country2: 'JP', country2Name: 'Japan',
    type: ['nuclear', 'territorial'],
    intensity: 'high',
    description: 'DPRK ballistic missiles overfly Japan; abductee issue; nuclear threat to Japanese cities.',
    since: '1998',
    activeMilitary: false,
    keyIssues: ['Missile overflights EEZ', 'Abductee resolution', 'Japanese counter-strike capability', 'Pyongyang-Tokyo backchannel'],
    latestIntel: 'Japan acquired Tomahawk missiles for counterstrike capability 2024. DPRK tested Hwasong-19 (20,000km range).',
  },
  {
    id: 'japan-sk',
    country1: 'JP', country1Name: 'Japan',
    country2: 'KR', country2Name: 'South Korea',
    type: ['ideological', 'economic'],
    intensity: 'medium',
    description: 'Historical grievances — forced labor, comfort women — complicated by shared US alliance needs.',
    since: '1945',
    activeMilitary: false,
    keyIssues: ['Forced labor reparations', 'Comfort women issue', 'Radar lock-on incident', 'Camp David trilateral 2023'],
    latestIntel: 'Yoon-Kishida Camp David reset 2023. Bilateral intel sharing expanded. China/DPRK threat forces cooperation.',
  },

  // ── Europe ─────────────────────────────────────────────────────────────────
  {
    id: 'turkey-greece',
    country1: 'TR', country1Name: 'Turkey',
    country2: 'GR', country2Name: 'Greece',
    type: ['territorial', 'economic'],
    intensity: 'medium',
    description: 'Aegean airspace/sea disputes; Erdogan challenges NATO ally Greece on continental shelf.',
    since: '1974',
    activeMilitary: false,
    keyIssues: ['Aegean airspace violations', 'Continental shelf demarcation', 'Cyprus occupation', 'F-35 vs S-400 row'],
    latestIntel: 'Greece upgraded US basing agreements. Turkey blocked Sweden NATO entry 18 months. Ongoing air intercepts.',
  },
  {
    id: 'armenia-azerbaijan',
    country1: 'AM', country1Name: 'Armenia',
    country2: 'AZ', country2Name: 'Azerbaijan',
    type: ['war', 'territorial'],
    intensity: 'medium',
    description: 'Nagorno-Karabakh: Azerbaijan seized enclave Sep 2023. Armenian diaspora displaced. Peace deal fragile.',
    since: '1991',
    activeMilitary: false,
    keyIssues: ['Nagorno-Karabakh final status', 'Zangezur corridor', 'Armenian-Russian trust collapse', 'EU monitoring mission'],
    latestIntel: 'NKR dissolved Oct 2023. ~100K Armenians fled to Armenia. Azerbaijan peace deal negotiations ongoing.',
  },

  // ── Africa ─────────────────────────────────────────────────────────────────
  {
    id: 'ethiopia-eritrea',
    country1: 'ET', country1Name: 'Ethiopia',
    country2: 'ER', country2Name: 'Eritrea',
    type: ['war', 'territorial'],
    intensity: 'medium',
    description: 'Tigray War involving Eritrea. Peace deal Nov 2022 but Eritrean forces still in Tigray.',
    since: '1998',
    activeMilitary: false,
    keyIssues: ['Eritrean forces in Tigray', 'Tigray humanitarian access', 'Abiy Nobel Peace Prize vs war crimes'],
    latestIntel: 'Pretoria agreement holding. Eritrean withdrawal incomplete. Amhara Fano insurgency ongoing.',
  },

  // ── Unique creative additions ───────────────────────────────────────────────
  {
    id: 'france-russia',
    country1: 'FR', country1Name: 'France',
    country2: 'RU', country2Name: 'Russia',
    type: ['sanctions', 'proxy-war', 'ideological'],
    intensity: 'high',
    description: 'Macron advocates EU strategic autonomy; strong Ukraine backer; threatened to send ground troops.',
    since: '2022',
    activeMilitary: false,
    keyIssues: ['Ukraine Legion training', 'Macron ground troops statement', 'French arms to Ukraine', 'Russian hybrid threats in Francophone Africa'],
    latestIntel: 'France expelled from Mali, Burkina, Niger by Wagner-backed juntas. Macron warned sending troops if frontline collapses.',
  },
  {
    id: 'uk-russia',
    country1: 'GB', country1Name: 'United Kingdom',
    country2: 'RU', country2Name: 'Russia',
    type: ['sanctions', 'proxy-war', 'cyber'],
    intensity: 'high',
    description: 'UK first to supply long-range missiles to Ukraine; Salisbury Novichok attack; hybrid warfare in UK.',
    since: '2018',
    activeMilitary: false,
    keyIssues: ['Storm Shadow strikes', 'Salisbury assassination fallout', 'Russian oligarch assets frozen', 'GRU Unit 29155 operations'],
    latestIntel: 'UK authorized Storm Shadow deep strikes into Russia Dec 2024. Comet cyber incidents linked to GRU.',
  },
  {
    id: 'india-china',
    country1: 'IN', country1Name: 'India',
    country2: 'CN', country2Name: 'China',
    type: ['territorial', 'economic'],
    intensity: 'high',
    description: 'World\'s two largest armies face each other along 3,488km LAC with unresolved borders.',
    since: '1962',
    activeMilitary: false,
    keyIssues: ['Depsang Plains LAC', 'Doka La Trijunction', 'Chinese ports encircling India', 'BRI vs QUAD'],
    latestIntel: 'Partial disengagement at Depsang 2024 after 4 years. India banning Chinese apps/investment. Modi-Xi Kazan summit sidelines.',
    simulationEnabled: true,
  },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function fetchCountryRivalries(): CountryRivalry[] {
  return COUNTRY_RIVALRIES;
}

/** Get all rivalries involving a country (by ISO code) */
export function getRivalriesForCountry(code: string): CountryRivalry[] {
  return COUNTRY_RIVALRIES.filter(
    (r) => r.country1 === code || r.country2 === code,
  );
}

/** Get rivalry between two specific countries */
export function getRivalryBetween(code1: string, code2: string): CountryRivalry | undefined {
  return COUNTRY_RIVALRIES.find(
    (r) => (r.country1 === code1 && r.country2 === code2) ||
            (r.country1 === code2 && r.country2 === code1),
  );
}

// ---------------------------------------------------------------------------
// Scenario simulation engine — client-side risk scoring (PREMIUM feature)
// ---------------------------------------------------------------------------

export interface ScenarioResult {
  type: 'military' | 'sanctions' | 'diplomatic' | 'alliance';
  title: string;
  riskScore: number;      // 0-100
  riskLabel: string;
  keyFactors: string[];
  outcome: string;
  isPremium: boolean;
}

function clamp(v: number): number { return Math.max(0, Math.min(100, v)); }

import type { CountryProfile } from '@/types';

export function computeScenarios(a: CountryProfile, b: CountryProfile): ScenarioResult[] {
  const existingRivalry = getRivalryBetween(a.code, b.code);
  const powerDiff = Math.abs(a.powerIndex - b.powerIndex);
  const bothNuclear = a.nuclearStatus === 'possesses' && b.nuclearStatus === 'possesses';
  const sharedAlliances = a.alliances.filter((al) => b.alliances.includes(al));
  const allianceScore = sharedAlliances.length;

  // Military conflict risk
  const rivalryBonus = existingRivalry?.activeMilitary ? 30 : existingRivalry ? 15 : 0;
  const nuclearDeterrence = bothNuclear ? -25 : 0;
  const militaryRisk = clamp(
    30 + rivalryBonus + (a.instabilityRisk === 'critical' ? 15 : a.instabilityRisk === 'high' ? 8 : 0)
    + (b.instabilityRisk === 'critical' ? 15 : b.instabilityRisk === 'high' ? 8 : 0)
    + (powerDiff < 10 ? 10 : 0) + nuclearDeterrence
    - allianceScore * 10,
  );

  // Sanctions impact
  const aGdp = a.gdpBillionUSD;
  const bGdp = b.gdpBillionUSD;
  const totalGdp = aGdp + bGdp;
  const sanctionsImpact = clamp(
    20 + (existingRivalry?.type.includes('sanctions') ? 20 : 0)
    + (aGdp > 5000 || bGdp > 5000 ? 20 : 10)
    + (a.geopoliticalStance !== b.geopoliticalStance ? 15 : -10)
    + (totalGdp > 30000 ? -10 : 5),
  );

  // Diplomatic break
  const stanceGap = a.geopoliticalStance !== b.geopoliticalStance ? 20 : -10;
  const diplomaticRisk = clamp(
    25 + stanceGap + (existingRivalry ? 15 : 0)
    + (a.leader.riskLevel === 'critical' || b.leader.riskLevel === 'critical' ? 20 : 0)
    - allianceScore * 12,
  );

  // Alliance formation probability
  const allianceProb = clamp(
    allianceScore * 15
    + (a.geopoliticalStance === b.geopoliticalStance ? 20 : -15)
    + (existingRivalry && existingRivalry.intensity === 'critical' ? -30 : 0)
    + (a.instabilityRisk === 'low' && b.instabilityRisk === 'low' ? 10 : -5),
  );

  return [
    {
      type: 'military',
      title: 'Military Conflict',
      riskScore: militaryRisk,
      riskLabel: militaryRisk >= 70 ? 'SEVERE' : militaryRisk >= 50 ? 'HIGH' : militaryRisk >= 30 ? 'MODERATE' : 'LOW',
      keyFactors: [
        bothNuclear ? 'Nuclear deterrence active' : 'No mutual deterrence',
        `Power gap: ${powerDiff}/100`,
        existingRivalry?.activeMilitary ? 'Active military conflict' : 'No active conflict',
        `Alliance overlap: ${allianceScore} shared`,
      ],
      outcome: militaryRisk >= 70
        ? 'Conventional war probable within 24 months under current trajectory.'
        : militaryRisk >= 40
          ? 'Escalatory incidents likely; proxy conflict possible.'
          : 'Military conflict unlikely; diplomatic deterrence sufficient.',
      isPremium: true,
    },
    {
      type: 'sanctions',
      title: 'Economic Warfare',
      riskScore: sanctionsImpact,
      riskLabel: sanctionsImpact >= 70 ? 'SEVERE' : sanctionsImpact >= 50 ? 'HIGH' : sanctionsImpact >= 30 ? 'MODERATE' : 'LOW',
      keyFactors: [
        `${a.name} GDP: $${(aGdp / 1000).toFixed(1)}T`,
        `${b.name} GDP: $${(bGdp / 1000).toFixed(1)}T`,
        existingRivalry?.type.includes('sanctions') ? 'Sanctions precedent exists' : 'No prior sanctions',
        `Geopolitical alignment: ${a.geopoliticalStance} vs ${b.geopoliticalStance}`,
      ],
      outcome: sanctionsImpact >= 70
        ? 'Severe economic decoupling. Supply chain disruption global.'
        : sanctionsImpact >= 40
          ? 'Targeted sanctions plausible. Limited GDP impact.'
          : 'Economic sanctions unlikely. Trade incentives outweigh conflict.',
      isPremium: true,
    },
    {
      type: 'diplomatic',
      title: 'Diplomatic Break',
      riskScore: diplomaticRisk,
      riskLabel: diplomaticRisk >= 70 ? 'SEVERE' : diplomaticRisk >= 50 ? 'HIGH' : diplomaticRisk >= 30 ? 'MODERATE' : 'LOW',
      keyFactors: [
        `Stance: ${a.geopoliticalStance} ↔ ${b.geopoliticalStance}`,
        `${a.name} leader risk: ${a.leader.riskLevel}`,
        `${b.name} leader risk: ${b.leader.riskLevel}`,
        existingRivalry ? `Existing rivalry: ${existingRivalry.intensity}` : 'No formal rivalry',
      ],
      outcome: diplomaticRisk >= 70
        ? 'Embassy closures, expulsions, trade missions suspended likely.'
        : diplomaticRisk >= 40
          ? 'Ambassador recall possible. Back-channel diplomacy required.'
          : 'Stable diplomatic relations. Normal embassy operations.',
      isPremium: true,
    },
    {
      type: 'alliance',
      title: 'Alliance Formation',
      riskScore: allianceProb,
      riskLabel: allianceProb >= 70 ? 'HIGH PROB' : allianceProb >= 50 ? 'LIKELY' : allianceProb >= 30 ? 'POSSIBLE' : 'UNLIKELY',
      keyFactors: [
        `Shared alliances: ${sharedAlliances.join(', ') || 'none'}`,
        `Geopolitical alignment: ${a.geopoliticalStance === b.geopoliticalStance ? '✓ aligned' : '✗ divergent'}`,
        `${a.name} instability: ${a.instabilityRisk}`,
        `${b.name} instability: ${b.instabilityRisk}`,
      ],
      outcome: allianceProb >= 60
        ? 'Formal alliance or treaty expansion highly probable.'
        : allianceProb >= 35
          ? 'Bilateral security cooperation MOU possible.'
          : 'Alliance formation unlikely. Competing interests dominate.',
      isPremium: true,
    },
  ];
}
